import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import {
  StudioSceneBackgroundDefinition,
  StudioScene,
  StudioImage,
  ImageAnimation,
} from '../../../element-editor/store/studio/studio.types';
import {
  selectStudioImages,
} from '../../../element-editor/store/studio/studio.selector';
import {
  BackgroundType,
} from '../../../element-editor/store/studio/background/background.service';
import { GradientDirection } from '../../../element-editor/shape/shapes/primitive/rectangle-element';
import { cloneDeep } from 'lodash';

export type BgEditorType = 'image' | 'gloves' | 'gradient' | 'solid';

@Component({
  selector: 'nw-background-edit-preview',
  templateUrl: './background-edit-preview.component.html',
  styleUrls: ['./background-edit-preview.component.scss'],
})
export class BackgroundEditPreviewComponent implements OnInit, OnDestroy {
  @Input() scene: StudioScene | null = null;

  @Output() backgroundChange = new EventEmitter<StudioSceneBackgroundDefinition>();
  @Output() cropChange = new EventEmitter<ImageAnimation['crop']>();

  // Type selector
  types: { label: string; key: BgEditorType; icon: string }[] = [
    { label: 'Image', key: 'image', icon: 'image' },
    { label: 'Glove', key: 'gloves', icon: 'blur_on' },
    { label: 'Gradient', key: 'gradient', icon: 'gradient' },
    { label: 'Solid', key: 'solid', icon: 'format_color_fill' },
  ];
  selectedType: BgEditorType = 'image';

  // Expose enum to template
  GradientDirection = GradientDirection;

  // Crop mode
  showCrop = false;

  // Images from store
  images: StudioImage[] = [];
  private imagesSub: Subscription;

  // Working background definition
  get bgDef(): StudioSceneBackgroundDefinition | undefined {
    return this.scene?.background;
  }

  // Gradient form
  gradientColors: string[] = ['#3b82f6', '#8b5cf6'];
  gradientDirection: GradientDirection = GradientDirection.Diagonal;

  // Solid fill
  solidColor = '#ffffff';

  // Preview scene (clone with background changes applied)
  previewScene: StudioScene | null = null;

  constructor(private store: Store) {}

  ngOnInit() {
    this.imagesSub = this.store
      .select(selectStudioImages)
      .subscribe(imgs => {
        this.images = Object.values(imgs || {});
      });

    this.initFromScene();
  }

  private initFromScene() {
    if (!this.scene) return;
    this.previewScene = cloneDeep(this.scene);

    const bg = this.scene.background;
    if (!bg) {
      this.selectedType = 'image';
      return;
    }

    switch (bg.type) {
      case BackgroundType.Image:
        this.selectedType = 'image';
        break;
      case BackgroundType.Gloves:
        this.selectedType = 'gloves';
        break;
      case BackgroundType.Gradient:
        this.selectedType = 'gradient';
        if (bg.gradient?.colors?.length) {
          this.gradientColors = [...bg.gradient.colors];
        }
        if (bg.gradient?.direction) {
          this.gradientDirection = bg.gradient.direction;
        }
        break;
      default:
        this.selectedType = 'solid';
        break;
    }
  }

  selectType(type: BgEditorType) {
    this.selectedType = type;
    this.showCrop = false;
  }

  // --- Image selection ---

  get selectedImageUrl(): string | null {
    return this.bgDef?.image?.url ?? null;
  }

  selectImage(image: StudioImage) {
    const def: StudioSceneBackgroundDefinition = {
      type: BackgroundType.Image,
      image: { url: image.url, fit: 'cover' },
    };
    if (this.bgDef?.animation) {
      def.animation = this.bgDef.animation;
    }
    this.emitChange(def);
  }

  toggleCrop() {
    this.showCrop = !this.showCrop;
  }

  onCropChange(crop: ImageAnimation['crop']) {
    this.cropChange.emit(crop);
  }

  // --- Gradient ---

  onGradientColorChange(index: number, color: string) {
    this.gradientColors[index] = color;
    this.applyGradient();
  }

  onGradientDirectionChange(dir: GradientDirection) {
    this.gradientDirection = dir;
    this.applyGradient();
  }

  private applyGradient() {
    const start: [number, number] = [0, 0];
    let end: [number, number];
    switch (this.gradientDirection) {
      case GradientDirection.Horizontal:
        end = [1, 0];
        break;
      case GradientDirection.Vertical:
        end = [0, 1];
        break;
      case GradientDirection.Diagonal:
      default:
        end = [1, 1];
        break;
    }

    const def: StudioSceneBackgroundDefinition = {
      type: BackgroundType.Gradient,
      gradient: {
        type: 'linear',
        direction: this.gradientDirection,
        colors: [...this.gradientColors],
        start,
        end,
      },
    };
    this.emitChange(def);
  }

  // --- Solid fill ---

  onSolidColorChange(color: string) {
    this.solidColor = color;
    const def: StudioSceneBackgroundDefinition = {
      type: BackgroundType.Gradient,
      gradient: {
        type: 'linear',
        colors: [color, color],
        start: [0, 0],
        end: [1, 0],
      },
    };
    this.emitChange(def);
  }

  // --- Emit ---

  private emitChange(def: StudioSceneBackgroundDefinition) {
    this.backgroundChange.emit(def);
    // Update preview scene
    if (this.previewScene) {
      this.previewScene = { ...this.previewScene, background: def };
    }
  }

  getImageThumbUrl(image: StudioImage): string {
    const url = image.url || '';
    if (url.startsWith('http')) return url;
    return `https://private.cdn.inwedio.com/${url}`;
  }

  ngOnDestroy() {
    this.imagesSub?.unsubscribe();
  }
}
